import React, { useState, useCallback } from 'react';

const RequestCopyForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const isFormValid = formData.firstName.trim() &&
    formData.lastName.trim() &&
    formData.email.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
    formData.jobTitle.trim();

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.firstName.trim()) {
      setError('Please enter your first name.');
      return;
    }
    if (!formData.lastName.trim()) {
      setError('Please enter your last name.');
      return;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!formData.jobTitle.trim()) {
      setError('Please enter your job title.');
      return;
    }

    // Create submission data
    const submissionData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      jobTitle: formData.jobTitle,
      timestamp: new Date().toISOString()
    };

    // Log to console (in production, send to server)
    console.log('Copy request submitted:', submissionData);

    // Store in localStorage for persistence
    const existingRequests = JSON.parse(localStorage.getItem('nextMethodCopyRequests') || '[]');
    existingRequests.push(submissionData);
    localStorage.setItem('nextMethodCopyRequests', JSON.stringify(existingRequests));

    // Trigger automatic PDF download
    const link = document.createElement('a');
    link.href = '/The_NEXT_Method.pdf';
    link.download = 'The_NEXT_Method.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Show success screen
    setSubmitted(true);
  }, [formData]);

  // Success screen with download
  if (submitted) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-dark-teal-400 to-dark-teal-600 text-white px-8 py-10 text-center">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
          <p className="text-dark-teal-100">Your download has started.</p>
        </div>

        <div className="p-8 text-center">
          <p className="text-gray-600 mb-6">
            If the download didn't start automatically,<br />
            click the button below.
          </p>
          <a
            href="/The_NEXT_Method.pdf"
            download="The_NEXT_Method.pdf"
            className="inline-flex justify-center items-center gap-2 bg-dark-dark-teal-400 hover:bg-dark-dark-teal-600 text-white rounded-full text-base h-12 py-0 px-8 transition-all duration-200 cursor-pointer font-semibold"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download PDF
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-dark-teal-400 to-dark-teal-600 text-white px-8 py-8 text-center">
        <h2 className="text-xl md:text-2xl font-bold mb-2">Request Your Copy</h2>
        <p className="text-dark-teal-100">The NEXT Method</p>
      </div>

      <div className="p-6 md:p-8">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* First Name */}
          <div className="mb-5">
            <label className="block text-gray-700 font-semibold mb-2">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder="Enter your first name"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-dark-teal-400 focus:ring-2 focus:ring-dark-teal-100 transition-all"
            />
          </div>

          {/* Last Name */}
          <div className="mb-5">
            <label className="block text-gray-700 font-semibold mb-2">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder="Enter your last name"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-dark-teal-400 focus:ring-2 focus:ring-dark-teal-100 transition-all"
            />
          </div>

          {/* Email */}
          <div className="mb-5">
            <label className="block text-gray-700 font-semibold mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="your.email@example.com"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-dark-teal-400 focus:ring-2 focus:ring-dark-teal-100 transition-all"
            />
          </div>

          {/* Job Title */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2">
              Job Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.jobTitle}
              onChange={(e) => handleInputChange('jobTitle', e.target.value)}
              placeholder="e.g., CEO, Board Chair, CFO"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-dark-teal-400 focus:ring-2 focus:ring-dark-teal-100 transition-all"
            />
          </div>

          {/* Download Now Button */}
          <button
            type="submit"
            disabled={!isFormValid}
            className={`w-full py-4 rounded-lg font-semibold text-lg transition-all ${
              isFormValid
                ? 'bg-dark-dark-teal-400 hover:bg-dark-dark-teal-600 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Download Now
          </button>
        </form>
      </div>
    </div>
  );
};

export default RequestCopyForm;
